import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { geoApi } from '../lib/api'
import type { GeoLiveMetricsRequest, GeoMetricsRequest, GeoUnitsParams, GeoUnitTypeKey } from '../types/geoAnalytics'

/** Boundary polygons change rarely — cache aggressively. */
export function useGeoUnits(params: GeoUnitsParams | null) {
  return useQuery({
    queryKey: ['geo', 'units', params],
    queryFn: () => geoApi.getUnits(params!).then(r => r.data),
    enabled: !!params && !!params.state,
    staleTime: 10 * 60_000,
    placeholderData: keepPreviousData,
  })
}

/** Cheap version token for (type, state) — threaded into useGeoUnits' params so a data
 * change (new ingest) produces a new query key + URL and bypasses the long HTTP cache. */
export function useGeoUnitsVersion(params: { type: string; state: string } | null) {
  return useQuery({
    queryKey: ['geo', 'units-version', params],
    queryFn: () => geoApi.getUnitsVersion(params!).then(r => r.data.version),
    enabled: !!params,
    staleTime: 0,
  })
}

export function useGeoMetrics(request: GeoMetricsRequest | null) {
  return useQuery({
    queryKey: ['geo', 'metrics', request],
    queryFn: () => geoApi.getMetrics(request!).then(r => r.data),
    enabled: !!request && request.metricKeys.length > 0,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  })
}

export function useGeoUnitProfile(unitId: string | null) {
  return useQuery({
    queryKey: ['geo', 'unit-profile', unitId],
    queryFn: () => geoApi.getUnitProfile(unitId!).then(r => r.data),
    enabled: !!unitId,
    staleTime: 60_000,
  })
}

export function useGeoLiveMetrics(request: GeoLiveMetricsRequest | null) {
  return useQuery({
    queryKey: ['geo', 'live-metrics', request],
    queryFn: () => geoApi.getLiveMetrics(request!).then(r => r.data),
    enabled: !!request && !!request.state,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  })
}

export function useGeoMetricKeys(params: { type: GeoUnitTypeKey; state: string } | null) {
  return useQuery({
    queryKey: ['geo', 'metric-keys', params],
    queryFn: () => geoApi.getMetricKeys(params!).then(r => r.data),
    enabled: !!params && !!params.state,
    staleTime: 5 * 60_000,
  })
}

/** Committee default-map-district bounding box (Task 9's `GET /api/geo/district-bounds`) —
 * used ONLY for the one-time default-view zoom (Task 10). 404/empty resolves to `null`
 * data rather than throwing, so the page can fall back to the state-extent zoom cleanly. */
export function useDistrictBounds(
  params: { state: string; type: string; code: string } | null,
) {
  return useQuery({
    queryKey: ['geo', 'district-bounds', params?.state, params?.type, params?.code],
    queryFn: async () => {
      try {
        const res = await geoApi.getDistrictBounds(params!.state, params!.type, params!.code)
        return res.data && res.data.length === 4 ? res.data : null
      } catch {
        return null
      }
    },
    enabled: !!params,
    staleTime: 60 * 60_000,
    retry: false,
  })
}

/** In-district worksites grouped by employer for the Union Footprint drill-down.
 *  Lazy: pass { enabled: isOpen } so nothing fetches until the modal opens. */
export function useUnionFootprintDetail(
  unitId: string | null,
  options?: { enabled?: boolean },
) {
  const enabled = (options?.enabled ?? true) && !!unitId
  return useQuery({
    queryKey: ['geo', 'union-footprint', unitId],
    queryFn: () => geoApi.getUnionFootprintDetail(unitId!).then((r) => r.data),
    enabled,
    staleTime: 60_000,
  })
}
